import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BrandLogo } from "@/components/BrandLogo";
import { LiveStats } from "@/components/LiveStats";
import { ExamplePhoto } from "@/components/ExamplePhoto";
import { ContactEmail } from "@/components/ContactEmail";
import {
  Home, ArrowRight, Video, Gavel, BadgeCheck, MapPin, DoorOpen, Download, ShieldCheck, Star, CalendarRange,
  Sparkles, ClipboardList, Lock, Bell, CheckCircle2,
} from "lucide-react";

const QUOTE_URL = "/auth?mode=signup&role=customer&next=/post-job";
export const MONT = { fontFamily: "'Montserrat', sans-serif" };

function QuoteButton({ testid, className = "" }) {
  const navigate = useNavigate();
  return (
    <Button size="lg" onClick={() => navigate(QUOTE_URL)} data-testid={testid}
      className={`bg-kinetic hover:bg-kinetic-hover text-white gap-2 h-12 px-7 text-base font-bold shadow-lg shadow-kinetic/30 hover:-translate-y-0.5 transition-transform ${className}`}>
      <Home className="h-5 w-5" /> Get a Move Quote
    </Button>
  );
}

export const Eyebrow = ({ children }) => <p className="font-mono text-xs uppercase tracking-widest text-kinetic mb-3">{children}</p>;
export const Example = () => <span className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded">Example</span>;

function HeroMock() {
  const bids = [["Removalist A", "$1,180", "4.9", "Sat 14 Nov"], ["Removalist B", "$1,260", "4.8", "Sun 15 Nov"], ["Removalist C", "$1,345", "5.0", "Sat 14 Nov"]];
  return (
    <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }} data-testid="movers-hero-mock">
      <div className="absolute -inset-4 bg-kinetic/15 rounded-[2rem] blur-3xl" />
      <div className="relative rounded-2xl border border-border bg-card shadow-2xl p-5 sm:p-6 space-y-4">
        <Example />
        <div>
          <p className="font-mono text-[10px] uppercase text-muted-foreground">Your move</p>
          <p className="font-bold text-lg" style={MONT}>3-bedroom house · 24.6 m³</p>
          <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-kinetic" /> Paddington → Manly</p>
        </div>
        <div className="space-y-2">
          {bids.map(([n, p, r, d], i) => (
            <div key={n} className={`flex items-center justify-between rounded-xl border p-3 animate-fade-up ${i === 0 ? "border-kinetic bg-kinetic-light" : "border-border"}`} style={{ animationDelay: `${300 + i * 150}ms` }}>
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-asphalt text-white flex items-center justify-center text-xs font-bold">{n.slice(-1)}</div>
                <div><p className="text-sm font-semibold">{n}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{r} · {d}</p></div>
              </div>
              <div className="text-right"><p className="font-bold" style={MONT}>{p}</p>{i === 0 && <p className="text-[10px] font-bold uppercase text-kinetic">Lowest bid</p>}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Hero() {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-14 pb-20 grid lg:grid-cols-2 gap-14 items-center">
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 mb-6" data-testid="movers-free-badge">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" /><span className="text-xs font-bold uppercase tracking-wider text-emerald-700">Free for customers</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-brandgrey" style={MONT} data-testid="movers-hero-heading">
          Moving house? <span className="text-kinetic">Let removalists compete</span> for your move.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
          Post your move once. Australian removalists send you bids, and you pick the price, date and mover that suit you. No phone tag, no chasing quotes.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <QuoteButton testid="movers-hero-quote-btn" />
          <button type="button" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} data-testid="movers-see-features-btn"
            className="text-sm font-semibold text-brandgrey hover:text-kinetic transition-colors flex items-center gap-1">See how it works <ArrowRight className="h-4 w-4" /></button>
        </div>
        <button type="button" onClick={() => navigate("/auth")} className="mt-5 text-sm text-muted-foreground hover:text-kinetic" data-testid="movers-login-link">Already posted a move? Log in</button>
      </div>
      <HeroMock />
    </section>
  );
}

function Steps() {
  const steps = [
    [ClipboardList, "Post your move", "Add your addresses, dates and what's moving — film each room and our AI builds the inventory for you."],
    [Gavel, "Removalists bid", "Removalists review your inventory and access details, then send you a price and date."],
    [CheckCircle2, "Pick the best offer", "Compare price, ratings and dates side by side. Accept one bid and you're booked."],
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="movers-steps">
      <Eyebrow>How it works</Eyebrow>
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-brandgrey mb-12 max-w-2xl" style={MONT}>Three steps from &ldquo;we're moving&rdquo; to booked.</h2>
      <div className="grid md:grid-cols-3 gap-6">
        {steps.map(([Icon, t, d], i) => (
          <div key={t} className="relative rounded-2xl border border-border bg-card p-7 hover:border-kinetic hover:-translate-y-1 transition-transform" data-testid={`movers-step-${i + 1}`}>
            <span className="absolute top-5 right-6 text-5xl font-black text-kinetic/15" style={MONT}>0{i + 1}</span>
            <div className="h-12 w-12 rounded-xl bg-kinetic-light flex items-center justify-center mb-5"><Icon className="h-6 w-6 text-kinetic" /></div>
            <h3 className="font-bold text-lg mb-2" style={MONT}>{t}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function InventoryMock() {
  const items = [["Queen Bed Setup", 1, ["Heavy", "Disassemble"]], ["Wall Mirror", 1, ["Fragile"]], ["Bedside Table", 2, []], ["Tea Chest Box", 8, []]];
  return (
    <div className="relative rounded-2xl border border-border bg-card p-5 shadow-xl space-y-3">
      <Example />
      <div className="flex flex-wrap gap-2">
        {["Master bedroom", "Kitchen", "Living room"].map((r) => <span key={r} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-kinetic-light text-xs border border-kinetic/30"><Video className="h-3 w-3 text-kinetic" />{r}</span>)}
      </div>
      <div className="rounded-xl border border-border divide-y divide-border">
        {items.map(([n, q, tags]) => (
          <div key={n} className="flex items-center justify-between p-3 text-sm">
            <span className="font-medium">{n} <span className="text-muted-foreground">×{q}</span></span>
            <span className="flex gap-1">{tags.map((t) => <span key={t} className={`text-[10px] px-1.5 py-0.5 rounded border ${t === "Fragile" ? "bg-rose-50 text-rose-700 border-rose-200" : t === "Heavy" ? "bg-slate-800 text-white border-slate-800" : "bg-sky-50 text-sky-700 border-sky-200"}`}>{t}</span>)}</span>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-asphalt text-white p-4 flex items-center justify-between">
        <div><p className="font-mono text-[10px] uppercase text-white/60">Total estimated volume</p><p className="text-2xl font-extrabold" style={MONT}>18.4 m³</p></div>
        <Sparkles className="h-7 w-7 text-kinetic" />
      </div>
    </div>
  );
}

export function MovePlanMock() {
  const rows = [["Master bedroom", "Back bedroom, ensuite side"], ["Bedroom 1", "Bedroom at front of house"], ["Living room", "Open-plan lounge"]];
  return (
    <div className="relative rounded-2xl border border-border bg-card p-5 shadow-xl space-y-4">
      <Example />
      <p className="font-bold" style={MONT}>Move plan</p>
      <div className="space-y-2">
        {rows.map(([a, b]) => <p key={a} className="text-sm flex flex-wrap gap-x-2"><span className="font-medium flex items-center gap-1.5"><DoorOpen className="h-4 w-4 text-kinetic" />{a}</span><span className="text-muted-foreground">→ {b}</span></p>)}
      </div>
      <div className="flex items-center gap-3 rounded-lg border border-border p-2 w-fit"><ExamplePhoto name="floor-plan" caption="Floor plan of the new home" className="h-12 w-12 rounded overflow-hidden bg-slate-200" /><span className="text-sm font-medium">new-home-floor-plan.pdf</span></div>
      <div className="inline-flex items-center gap-2 rounded-md bg-kinetic text-white text-sm font-medium px-4 py-2"><Download className="h-4 w-4" /> Download inventory PDF</div>
    </div>
  );
}

const ACCESS_SECTIONS = [["Pickup access", "text-emerald-500", "2nd floor walk-up, no lift. Loading zone before 10am.", [["pickup-stairs", "Stairs to 2nd floor"], ["pickup-loading", "Loading zone sign"]]], ["Drop-off access", "text-kinetic", "Ground floor, wide driveway fits a truck.", [["dropoff-front", "Ground floor entry"], ["dropoff-driveway", "Wide driveway"]]]];
const ACCESS_PHOTOS = ACCESS_SECTIONS.flatMap(([t, , , pics]) => pics.map(([f, alt]) => [f, `${t.replace(" access", "")}: ${alt}`]));

export function AccessMock() {
  return (
    <div className="relative rounded-2xl border border-border bg-card p-5 shadow-xl grid sm:grid-cols-2 gap-4">
      <Example />
      {ACCESS_SECTIONS.map(([t, c, n, pics]) => (
        <div key={t} className="space-y-2">
          <p className="font-mono text-[10px] uppercase text-muted-foreground flex items-center gap-1"><MapPin className={`h-3.5 w-3.5 ${c}`} />{t}</p>
          <p className="text-sm">{n}</p>
          <div className="flex gap-2">{pics.map(([f, alt]) => <ExamplePhoto key={f} name={f} caption={alt} group={ACCESS_PHOTOS} className="h-14 w-14 rounded-lg overflow-hidden bg-slate-200" />)}</div>
        </div>
      ))}
    </div>
  );
}

export function BidsMock() {
  return (
    <div className="relative rounded-2xl border border-border bg-card p-5 shadow-xl space-y-3">
      <Example />
      {[["$1,180", "4 hrs · 2 movers + 6t truck", "Sat 14 Nov", true], ["$1,260", "5 hrs · 3 movers", "Sun 15 Nov", false]].map(([p, d, dt, best]) => (
        <div key={p} className={`rounded-xl border p-4 ${best ? "border-kinetic" : "border-border"}`}>
          <div className="flex items-center justify-between"><p className="text-xl font-extrabold" style={MONT}>{p}</p><span className="text-xs text-muted-foreground flex items-center gap-1"><CalendarRange className="h-3.5 w-3.5" />{dt}</span></div>
          <p className="text-sm text-muted-foreground mt-1">{d}</p>
          {best && <div className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-asphalt text-white text-xs font-medium px-3 py-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Accept bid</div>}
        </div>
      ))}
    </div>
  );
}

const FEATURES = [
  { id: "inventory", icon: Video, eyebrow: "AI video inventory", title: "Film each room. We'll count the furniture.",
    body: "Record a short video per room. Our AI lists every item, groups it by room and works out the cubic metres, so every removalist quotes on the same accurate inventory. Tag anything fragile, heavy or that needs disassembly, and add your own notes.", Mock: InventoryMock },
  { id: "bids", icon: Gavel, eyebrow: "Compare bids", title: "Removalists compete. You choose.",
    body: "Bids arrive with a price, estimated hours, crew details and their available date. Compare them side by side with each removalist's ratings and profile, and get notified the moment a new bid lands.", Mock: BidsMock },
  { id: "access", icon: MapPin, eyebrow: "Access details", title: "No surprises on moving day.",
    body: "Add notes, photos or a short video of stairs, lifts, parking and tight corners at both your pickup and drop-off address, so removalists can price the job properly.", Mock: AccessMock },
  { id: "plan", icon: DoorOpen, eyebrow: "Room-by-room move plan", title: "Every box lands in the right room.",
    body: "Tell your removalist where each room goes in the new home and attach a floor plan. Your plan and a complete inventory PDF are shared only with the removalist you accept.", Mock: MovePlanMock },
];

function Features() {
  return (
    <section id="features" className="bg-muted/40 border-y border-border py-20" data-testid="movers-features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
        {FEATURES.map(({ id, icon: Icon, eyebrow, title, body, Mock }, i) => (
          <div key={id} className="grid lg:grid-cols-2 gap-12 items-center" data-testid={`movers-feature-${id}`}>
            <div className={i % 2 ? "lg:order-2" : ""}>
              <div className="h-11 w-11 rounded-xl bg-kinetic text-white flex items-center justify-center mb-5"><Icon className="h-5 w-5" /></div>
              <Eyebrow>{eyebrow}</Eyebrow>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-brandgrey mb-4" style={MONT}>{title}</h2>
              <p className="text-muted-foreground leading-relaxed max-w-lg">{body}</p>
            </div>
            <div className={i % 2 ? "lg:order-1" : ""}><Mock /></div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TrustGrid() {
  const items = [
    [BadgeCheck, "Real business profiles", "Removalists list their business name and can add their ABN or licence, logo, website and insurance cover."],
    [Lock, "Your details stay private", "Your contact details are only shared with the removalist whose bid you accept."],
    [Star, "Ratings & reviews", "See each removalist's rating, reviews, website and insurance details on their profile."],
    [CalendarRange, "Flexible dates", "Tell removalists how flexible your move date is to unlock better prices."],
    [Bell, "Instant notifications", "Get an email and in-app alert whenever a new bid comes in."],
    [ShieldCheck, "Free for customers", "Posting a move and receiving bids costs you nothing."],
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="movers-trust">
      <Eyebrow>Why MoveTender</Eyebrow>
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-brandgrey mb-12" style={MONT}>Built for a stress-free move.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(([Icon, t, d]) => (
          <div key={t} className="flex gap-4 rounded-xl p-5 hover:bg-kinetic-light/60 transition-colors">
            <Icon className="h-6 w-6 text-kinetic shrink-0 mt-0.5" />
            <div><h3 className="font-bold mb-1" style={MONT}>{t}</h3><p className="text-sm text-muted-foreground leading-relaxed">{d}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
}

const FAQS = [
  ["Does it cost anything to post my move?", "No. Posting a move and receiving bids is free for customers."],
  ["Do I have to film a video?", "No. Video makes the inventory faster and more accurate, but you can add rooms and items manually or just describe what's moving."],
  ["Who can see my address and contact details?", "Removalists see your move details and inventory to quote. Your contact details, move plan and floor plan are only shared with the removalist whose bid you accept."],
  ["Am I locked in once bids arrive?", "No. You only commit when you accept a bid, and you can compare as many offers as you receive first."],
  ["What does m³ mean on my inventory?", "It's the estimated cubic volume of everything you're moving, including a 15% packing buffer. Removalists use it to choose the right truck and crew."],
];

function Faq() {
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="movers-faq">
      <Eyebrow>FAQ</Eyebrow>
      <h2 className="text-3xl font-extrabold tracking-tight text-brandgrey mb-8" style={MONT}>Questions, answered.</h2>
      <Accordion type="single" collapsible>
        {FAQS.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`}>
            <AccordionTrigger className="text-left font-semibold" data-testid={`movers-faq-q-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export default function Movers() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background" data-testid="movers-landing-page">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 sm:h-28 flex items-center justify-between gap-3">
        <button type="button" onClick={() => navigate("/")} data-testid="movers-brand-link"><BrandLogo size="lg" /></button>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button variant="ghost" onClick={() => navigate("/auth")} data-testid="movers-header-login-btn" className="px-2 sm:px-4">Log in</Button>
          <Button onClick={() => navigate(QUOTE_URL)} data-testid="movers-header-quote-btn" className="bg-kinetic hover:bg-kinetic-hover text-white px-3 sm:px-4">Get a quote</Button>
        </div>
      </header>
      <Hero />
      <LiveStats />
      <Steps />
      <Features />
      <TrustGrid />
      <Faq />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-asphalt text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-8" data-testid="movers-final-cta">
          <img src="/logo-mark.png" alt="" className="absolute -right-10 -bottom-10 w-64 opacity-10 pointer-events-none" />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={MONT}>Ready to move?</h2>
            <p className="text-white/70 mt-2 max-w-md">Post your move in minutes and let removalists come to you. Free for customers.</p>
          </div>
          <QuoteButton testid="movers-final-quote-btn" className="relative" />
        </div>
      </section>
      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">MoveTender — Australia's Moving Marketplace · <ContactEmail /></footer>
    </div>
  );
}
